import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Papa from "papaparse";
import { DateTime } from "luxon";

import Card from "../../components/shared/Card/Card";
import ClickToCopy from "../../components/shared/ClickToCopy";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import StatusPill, { StatusPillColour } from "../../components/shared/StatusPill";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useStockCheckReports from "../../data/useStockCheckReports";
import { PartStatus } from "../../models/Part";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import {
  STOCK_CHECK_REPORT_CSV_HEADERS,
  stockCheckPartStatusLabel,
  stockCheckReportCsvRow,
} from "../../utils/stockCheckReport";

function statusColour(status: PartStatus): StatusPillColour {
  switch (status) {
    case PartStatus.Dismantled:
      return StatusPillColour.Green;
    case PartStatus.QC:
      return StatusPillColour.Blue;
    case PartStatus.Missing:
    case PartStatus.Damaged:
    case PartStatus["Not Accessible"]:
      return StatusPillColour.Red;
    case PartStatus.Sold:
      return StatusPillColour.Purple;
    default:
      return StatusPillColour.Neutral;
  }
}

export default function ViewStockCheckReport() {
  const { setInfo } = usePageNameContext();
  const { stockCheckId } = useParams();
  const { report } = useStockCheckReports(1, 25, stockCheckId);
  const data = report.data?.data;

  useEffect(() => {
    setInfo({
      name: data ? `Stock Check — ${data.warehouseLocationName}` : "Stock Check Report",
      desc: data
        ? `${data.itemCount} part${data.itemCount === 1 ? "" : "s"} checked`
        : "Stock check saved from the yard app",
    });
  }, [setInfo, data]);

  if (report.isLoading) {
    return <LoadingWheel />;
  }

  if (!data) {
    return <p className="mt-4 text-sm text-gray-500">That stock check report could not be found.</p>;
  }

  const handleDownload = () => {
    const csv = Papa.unparse({
      fields: STOCK_CHECK_REPORT_CSV_HEADERS,
      data: data.items.map(stockCheckReportCsvRow),
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const completed = formatDateTime(data.completed, DateTime.DATE_SHORT).replace(/\//g, "-");
    link.href = url;
    link.download = `stock-check-${data.warehouseLocationName}-${completed}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-4 grid grid-cols-1 gap-4">
      <Card
        title={data.warehouseLocationName}
        subtitle={`Completed ${formatDateTime(data.completed, DateTime.DATETIME_SHORT)}${
          data.completedByEmail ? ` by ${data.completedByEmail}` : ""
        }`}
        headerRight={
          <ButtonInput label="Download CSV" classes="" onClick={handleDownload} />
        }
      >
        <div className="overflow-auto">
          <table className="min-w-full divide-y border divide-gray-300">
            <thead>
              <tr className="bg-primary-50 text-gray-800">
                <th className="py-3.5 px-3 text-sm font-semibold text-left">Tag number</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">Part category</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">Has images</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">On eBay</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">Part status</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">Part age</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">Last stock check</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">Part value</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">MPN</th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">OEMPN</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 border text-sm">
                    <Link
                      to={`/stock/${item.partDataId}`}
                      className="text-primary-700 hover:text-primary-800 font-medium"
                    >
                      {item.tagNumber || item.partDataId}
                    </Link>
                  </td>
                  <td className="px-3 py-3 border text-sm">{item.categoryName || "—"}</td>
                  <td className="px-3 py-3 border text-sm">
                    <StatusPill
                      text={item.hasImages ? "Yes" : "No"}
                      colour={item.hasImages ? StatusPillColour.Green : StatusPillColour.Neutral}
                    />
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    <StatusPill
                      text={item.isOnEbay ? "Yes" : "No"}
                      colour={item.isOnEbay ? StatusPillColour.Green : StatusPillColour.Neutral}
                    />
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    <StatusPill
                      text={stockCheckPartStatusLabel(item.partStatus)}
                      colour={statusColour(item.partStatus)}
                    />
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    {item.partAgeDays === 1 ? "1 day" : `${item.partAgeDays} days`}
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    {formatDateTime(item.previousStockChecked, DateTime.DATE_SHORT, "—")}
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    {item.partValue == null ? "—" : PriceFormat(item.partValue)}
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    {item.mpn ? <ClickToCopy text={item.mpn} /> : "—"}
                  </td>
                  <td className="px-3 py-3 border text-sm">
                    {item.oemPn ? <ClickToCopy text={item.oemPn} /> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
